import { get } from "aws-amplify/api";
import React, { useMemo, useState } from "react";
import { Button, Card, Col, Form, InputGroup, Row } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import Select from "react-select";
import { createElementIdFactory } from "utils/elementIds";
import "../../common/Detail.css";

/**
 * Shared search block used for both agent and co-agent.
 */
function AgentSearchBlock({
  label,
  searchInputId,
  searchButtonId,
  interstateControlId,
  interstateInputId,
  selectInputId,
  addButtonId,
  editButtonId,
  searchValue,
  onSearchChange,
  onSearchKeyDown,
  onSearch,
  interstateChecked,
  onInterstateChange,
  selectedAgent,
  agentOptions,
  onAgentChange,
  menuIsOpen,
  saleId,
  entityId,
}) {
  return (
    <>
      <Form.Group as={Row}>
        <Form.Label className="text-right" column sm={6} md={6} lg={4} htmlFor={searchInputId}>
          Agency Search
        </Form.Label>
        <Col>
          <InputGroup size="sm">
            <Form.Control
              id={searchInputId}
              onChange={onSearchChange}
              value={searchValue}
              onKeyDown={onSearchKeyDown}
              as="input"
              type="text"
              placeholder="Search with % wildcard"
            />
            <InputGroup.Text as="span" style={{ padding: 0, border: 0, background: "transparent", marginLeft: "8px" }}>
              <Button id={searchButtonId} onClick={onSearch} variant="outline-secondary" size="sm">
                Search
              </Button>
            </InputGroup.Text>
          </InputGroup>
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Col sm={6} md={6} lg={4}></Col>
        <Col className="mt-2 pl-2">
          <Form.Check
            id={interstateInputId}
            checked={interstateChecked}
            onChange={onInterstateChange}
            label="Search interstate"
            type="checkbox"
            name={interstateControlId}
          />
        </Col>
      </Form.Group>

      <Form.Group as={Row}>
        <Form.Label className="text-right" column sm={6} md={6} lg={4} htmlFor={selectInputId}>
          {label}
        </Form.Label>
        <Col>
          <Select
            inputId={selectInputId}
            value={selectedAgent}
            onChange={onAgentChange}
            options={agentOptions}
            isClearable
            menuIsOpen={menuIsOpen}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Col>
      </Form.Group>

      <div style={{marginTop: '8px'}}>
      <Row>
        <Col sm={6} md={6} lg={4}></Col>
        <Col>
          <LinkContainer to={{ pathname: "../entities/new", search: `?saleid=${saleId}` }}>
            <Button id={addButtonId} size="sm" className="btn-teal">
              Add New Agent
            </Button>
          </LinkContainer>{" "}
          {entityId && (
            <LinkContainer to={{ pathname: `../entities/${entityId}`, search: `?saleid=${saleId}` }}>
              <Button id={editButtonId} size="sm" className="btn-teal">
                Edit Existing Agent
              </Button>
            </LinkContainer>
          )}
        </Col>
      </Row>
      </div>
    </>
  );
}

/**
 * AgentSelector
 *
 * Owns agent and co-agent search, selection, and display.
 * Makes its own API calls — the parent does not need to know about agent data.
 *
 * The only external dependency is `propertyState` (the state of the first
 * linked property) used to filter agents by geography. It is passed as a prop
 * so this component remains decoupled from PropertySaleList.
 *
 * Props:
 *   entityIdAgent    - initial agent entity ID
 *   agent            - initial agent label
 *   agentName        - initial agent name
 *   entityIdCoAgent  - initial co-agent entity ID
 *   coAgent          - initial co-agent label
 *   coAgentName      - initial co-agent name
 *   propertyState    - state abbreviation used for interstate filter
 *   saleId           - used in "Add/Edit Agent" navigation links
 *   onSave(patch)    - called when agent or co-agent selection changes
 *   onWarning(msg)   - called to surface advisory alerts to the parent
 */
function AgentSelector({
  entityIdAgent: initEntityIdAgent = null,
  agent: initAgent = null,
  agentName: initAgentName = null,
  entityIdCoAgent: initEntityIdCoAgent = null,
  coAgent: initCoAgent = null,
  coAgentName: initCoAgentName = null,
  propertyState = null,
  saleId,
  onSave,
  onWarning,
}) {
  const createId = useMemo(() => createElementIdFactory("sale-detail-agents"), []);

  // ── Agent ──────────────────────────────────────────────────────────────────
  const [agentSearchString, setAgentSearchString] = useState("");
  const [agentInterstateFlag, setAgentInterstateFlag] = useState(false);
  const [agentMenuIsOpen, setAgentMenuIsOpen] = useState(false);
  const [agentOptions, setAgentOptions] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(
    initEntityIdAgent ? { value: initEntityIdAgent, label: initAgent } : null
  );
  const [entityIdAgent, setEntityIdAgent] = useState(initEntityIdAgent);

  // ── Co-agent ───────────────────────────────────────────────────────────────
  const [coAgentSearchString, setCoAgentSearchString] = useState("");
  const [coAgentInterstateFlag, setCoAgentInterstateFlag] = useState(false);
  const [coAgentMenuIsOpen, setCoAgentMenuIsOpen] = useState(false);
  const [coAgentOptions, setCoAgentOptions] = useState([]);
  const [selectedCoAgent, setSelectedCoAgent] = useState(
    initEntityIdCoAgent ? { value: initEntityIdCoAgent, label: initCoAgent } : null
  );
  const [entityIdCoAgent, setEntityIdCoAgent] = useState(initEntityIdCoAgent);

  // ── API ────────────────────────────────────────────────────────────────────

  async function fetchEntities(filter) {
    try {
      const queryParams = Object.fromEntries(
        Object.entries(filter).filter(([, v]) => v != null)
      );
      const request = get({
        apiName: "colin",
        path: "/entities/dropdown",
        options: {
          queryParams,
        },
      });
      const { body } = await request.response;
      const result = await body.json();
      return result || [];
    } catch (e) {
      console.log("Error fetching entities", e);
      return [];
    }
  }

  // ── Agent handlers ─────────────────────────────────────────────────────────

  async function handleAgentSearch() {
    try {
      const state = agentInterstateFlag ? null : propertyState;
      const options = await fetchEntities({ name: agentSearchString, state });
      setAgentOptions(options);
      setAgentMenuIsOpen(true);
    } catch {
      alert("Error loading agents");
    }
  }

  function handleAgentChange(option) {
    setSelectedAgent(option);
    setEntityIdAgent(option ? option.value : null);
    setAgentMenuIsOpen(false);

    onSave({
      entity_id_agent: option ? option.value : null,
      agent: option ? option.label : null,
      agent_name: option ? option.name : null,
    });

    if (option) {
      if (option.label.indexOf("INACTIVE") > 0) {
        onWarning("You have selected an inactive agent, please consider if this is correct. The record will still be saved.");
      } else if (propertyState && option.label.toUpperCase().indexOf(propertyState.toUpperCase()) === -1) {
        onWarning("You have selected an agent in another state, please consider if this is correct. The record will still be saved.");
      }
    }
  }

  // ── Co-agent handlers ──────────────────────────────────────────────────────

  async function handleCoAgentSearch() {
    try {
      const state = coAgentInterstateFlag ? null : propertyState;
      const options = await fetchEntities({ name: coAgentSearchString, state });
      setCoAgentOptions(options);
      setCoAgentMenuIsOpen(true);
    } catch {
      alert("Error loading agents");
    }
  }

  function handleCoAgentChange(option) {
    setSelectedCoAgent(option);
    setEntityIdCoAgent(option ? option.value : null);
    setCoAgentMenuIsOpen(false);

    onSave({
      entity_id_co_agent: option ? option.value : null,
      co_agent: option ? option.label : null,
      co_agent_name: option ? option.name : null,
    });

    if (option) {
      if (option.label.indexOf("INACTIVE") > 0) {
        onWarning("You have selected an inactive co agent, please consider if this is correct. The record will still be saved.");
      } else if (propertyState && option.label.toUpperCase().indexOf(propertyState.toUpperCase()) === -1) {
        onWarning("You have selected a co agent in another state, please consider if this is correct. The record will still be saved.");
      }
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Card id={createId("card")}>
      <Card.Header>Agents</Card.Header>
      <Card.Body>
        <AgentSearchBlock
          label="Agent"
          searchInputId={createId("agent-search-input")}
          searchButtonId={createId("agent-search-button")}
          interstateControlId={createId("agent-interstate")}
          interstateInputId={createId("agent-interstate-input")}
          selectInputId={createId("agent-select-input")}
          addButtonId={createId("agent-add")}
          editButtonId={createId("agent-edit")}
          searchValue={agentSearchString}
          onSearchChange={(e) => setAgentSearchString(e.target.value)}
          onSearchKeyDown={(e) => e.keyCode === 13 && handleAgentSearch()}
          onSearch={handleAgentSearch}
          interstateChecked={agentInterstateFlag}
          onInterstateChange={(e) => setAgentInterstateFlag(e.target.checked)}
          selectedAgent={selectedAgent}
          agentOptions={agentOptions}
          onAgentChange={handleAgentChange}
          menuIsOpen={agentMenuIsOpen}
          saleId={saleId}
          entityId={entityIdAgent}
        />

        <br />
        <hr />
        <br />

        <AgentSearchBlock
          label="Co Agent"
          searchInputId={createId("co-agent-search-input")}
          searchButtonId={createId("co-agent-search-button")}
          interstateControlId={createId("co-agent-interstate")}
          interstateInputId={createId("co-agent-interstate-input")}
          selectInputId={createId("co-agent-select-input")}
          addButtonId={createId("co-agent-add")}
          editButtonId={createId("co-agent-edit")}
          searchValue={coAgentSearchString}
          onSearchChange={(e) => setCoAgentSearchString(e.target.value)}
          onSearchKeyDown={(e) => e.keyCode === 13 && handleCoAgentSearch()}
          onSearch={handleCoAgentSearch}
          interstateChecked={coAgentInterstateFlag}
          onInterstateChange={(e) => setCoAgentInterstateFlag(e.target.checked)}
          selectedAgent={selectedCoAgent}
          agentOptions={coAgentOptions}
          onAgentChange={handleCoAgentChange}
          menuIsOpen={coAgentMenuIsOpen}
          saleId={saleId}
          entityId={entityIdCoAgent}
        />
      </Card.Body>
    </Card>
  );
}

export default AgentSelector;
